import { useEffect, useCallback, useRef } from 'react';

/**

 * @description A custom React hook to automatically perform an action after a period of inactivity.
 * @param {object} options
 * @param {boolean} options.isLoggedIn - Controls whether the timer is active. The timer will only run if this is true.
 * @param {function} options.onTimeout - The function to call when the timer expires (e.g., a logout handler).
 * @param {number} [options.timeout=3300000] - The inactivity period in milliseconds. Defaults to 55 minutes.
 */
export function useInactivityTimer({ isLoggedIn, onTimeout, timeout }) {
  // useRef is used to hold the timer ID so it persists across re-renders without causing them.
  const timerId = useRef(null);


  const resetTimer = useCallback(() => {
    // Clear any existing timeout to prevent old timers from firing.
    if (timerId.current) {
      clearTimeout(timerId.current);
    }
    
    // Set a new timeout. When it completes, it will call the onTimeout callback.
    timerId.current = setTimeout(() => {
      onTimeout();
    }, timeout);
  }, [onTimeout, timeout]);

  useEffect(() => {
    // If the user is not logged in, we do nothing.
    if (!isLoggedIn) {
      return;
    }

    // A list of events that indicate the user is active.
    const events = [
      "mousemove", "mousedown", "touchstart", "click",
      "keydown", "keypress", "scroll", "load"
    ];

   
    // When any of these events fire, the resetTimer function is called.
    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    // Start the timer when the component mounts or when the user logs in.
    resetTimer();

    // This is the cleanup function. It runs when the component unmounts or when `isLoggedIn` becomes false.
    return () => {
      // Clear the timeout to prevent it from firing after the user has logged out.
      if (timerId.current) {
        clearTimeout(timerId.current);
      }
      
      // Remove all the event listeners to prevent memory leaks.
      events.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
    };
  }, [isLoggedIn, resetTimer]); // The effect depends on `isLoggedIn` and `resetTimer`. It will re-run if either changes.
}